import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, ArrowRight, Sparkles } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      toast.success("Selamat datang kembali 👋");
      nav("/");
    } catch (err) {
      // toast handled by axios interceptor
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, hsl(217 91% 60% / 0.4), transparent 40%), radial-gradient(circle at 80% 70%, hsl(262 83% 58% / 0.3), transparent 40%)"
        }} />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-white text-slate-900 grid place-items-center font-bold text-lg">
              LT
            </div>
            <div>
              <p className="text-xl font-bold tracking-tight">Loket Transaksi</p>
              <p className="text-sm text-slate-400">Pusat Kendali Usaha</p>
            </div>
          </div>
        </div>
        <div className="relative z-10 space-y-4">
          <h1 className="text-4xl xl:text-5xl font-bold tracking-tight leading-tight">
            Kelola semua<br />
            <span className="text-blue-400">transaksi digital</span><br />
            dalam satu dashboard.
          </h1>
          <p className="text-slate-300 text-base max-w-md">
            Pulsa, PLN, PPOB, BPJS, transfer — pantau saldo, keuntungan, kas fisik, dan utang tanpa buka banyak aplikasi.
          </p>
        </div>
        <div className="relative z-10 space-y-4">
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <Sparkles className="h-4 w-4 text-blue-400" />
            <span>AI analisis keuntungan otomatis</span>
          </div>
          <div className="flex items-center gap-5">
            <img
              src="/avatar.jpg"
              alt="Abdul Muis"
              className="w-40 h-40 object-cover object-center shrink-0 select-none pointer-events-none [mask-image:radial-gradient(circle_at_50%_42%,black_52%,transparent_74%)] [-webkit-mask-image:radial-gradient(circle_at_50%_42%,black_52%,transparent_74%)]"
            />
            <div className="leading-tight">
              <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Dibuat oleh</p>
              <p className="text-2xl font-bold text-white tracking-tight">Abdul Muis</p>
              <p className="text-sm text-slate-400 mt-0.5">Pengembang Website</p>
            </div>
          </div>
          <p className="text-xs text-slate-500">© 2026 Loket Transaksi. Aman, cepat, mobile-first.</p>
        </div>
      </div>

      {/* Login form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground grid place-items-center font-bold">
              LT
            </div>
            <div>
              <p className="font-bold text-lg tracking-tight">Loket Transaksi</p>
              <p className="text-xs text-muted-foreground">Pusat Kendali Usaha</p>
            </div>
          </div>

          <div className="space-y-1.5">
            <h2 className="text-3xl font-bold tracking-tight">Masuk ke akun Anda</h2>
            <p className="text-sm text-muted-foreground">
              Kelola loket transaksi dari mana saja.
            </p>
          </div>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                data-testid="login-username"
                required
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Masukkan username"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={show ? "text" : "password"}
                  data-testid="login-password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimal 6 karakter"
                  className="pr-10"
                />
                <button
                  type="button"
                  data-testid="login-show-password"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                  onClick={() => setShow(!show)}
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-11 text-sm font-semibold"
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Memproses...</>
              ) : (
                <>Masuk <ArrowRight className="ml-2 h-4 w-4" /></>
              )}
            </Button>
          </form>

          <div className="mt-8 lg:hidden flex items-center gap-4">
            <img
              src="/avatar.jpg"
              alt="Abdul Muis"
              className="w-24 h-24 rounded-full object-cover object-[center_35%] ring-2 ring-border shadow-md shrink-0 select-none pointer-events-none"
            />
            <div className="leading-tight">
              <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Dibuat oleh</p>
              <p className="text-xl font-bold tracking-tight">Abdul Muis</p>
              <p className="text-xs text-muted-foreground mt-0.5">Pengembang Website</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
